import { Box, Button, Flex, HStack, Text, VStack, chakra } from '@chakra-ui/react';
import { UserPlus } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { formatShortTime } from '@/lib/format-short-time';

interface ApplicationEventCardProps {
  kind: 'brief' | 'program';
  /** The applicant's full name; only her first name is said. Null when unknown. */
  creatorName: string | null;
  /** The brief title or the program name, denormalized at submission. */
  targetTitle: string;
  occurredAt: string;
  onOpen: () => void;
}

const firstName = (name: string | null): string | null => name?.trim().split(/\s+/)[0] || null;

/**
 * The application moment of a thread: who applied to what, the reminder that
 * applying commits nobody, and the way back to the application. Same frame and
 * pastille as the registry card; it carries the one button the design gives
 * it, because the application is something the brand acts on.
 */
const ApplicationEventCard = ({
  kind,
  creatorName,
  targetTitle,
  occurredAt,
  onOpen,
}: ApplicationEventCardProps) => {
  const { t, i18n } = useTranslation();
  const name = firstName(creatorName);
  const title = name
    ? t(`messages.applicationEvent.${kind}`, { name, title: targetTitle })
    : t(`messages.applicationEvent.${kind}Unnamed`, { title: targetTitle });
  const time = formatShortTime(occurredAt, i18n?.language);

  return (
    <Box
      role="note"
      bg="babyPowder"
      borderWidth="1px"
      borderColor="border"
      rounded="xl"
      px="13px"
      py="11px"
      w="full"
    >
      <HStack align="flex-start" gap="10px" w="full">
        <Flex
          aria-hidden="true"
          boxSize="25px"
          rounded="md"
          bg="softCream"
          color="primary.900"
          align="center"
          justify="center"
          flexShrink={0}
        >
          <UserPlus width={13} height={13} />
        </Flex>
        <VStack align="stretch" gap="2px" flex="1" minW={0}>
          <Text
            fontFamily="heading"
            fontWeight="semibold"
            fontSize="12.5px"
            lineHeight="1.3"
            color="primary.900"
          >
            {title}
          </Text>
          <Text fontSize="12px" lineHeight="1.45" color="navInactive">
            {time && (
              <>
                <chakra.time dateTime={occurredAt}>{time}</chakra.time>
                {' · '}
              </>
            )}
            <chakra.span>{t('messages.applicationEvent.note')}</chakra.span>
          </Text>
          <Box pt="6px">
            <Button size="xs" variant="outline" onClick={onOpen}>
              {t('messages.applicationEvent.open')}
            </Button>
          </Box>
        </VStack>
      </HStack>
    </Box>
  );
};

export default ApplicationEventCard;
